import type {
  CapabilityConfig,
  Message,
  ExtendedConstraints,
} from '../../../types';
import {
  hasValue,
  isValidMessageConfig,
  hasOnlyKeysFromSet,
} from '../../../utils';
import * as AppletSpec from '../applet/applet';

export enum Layout {
  Full = 'Full',
  LeftFixed300 = 'LeftFixed300',
  LeftHalf = 'LeftHalf',
}
export const DEFAULT_APPLET_LAYOUT = Layout.Full;
const appletLayoutValues = Object.values(Layout) as string[];

const validStatusTagStatuses = [
  'positive',
  'negative',
  'notice',
  'informative',
  'neutral',
] as const;
type StatusTagStatus = (typeof validStatusTagStatuses)[number];

export type UpdateHeaderPayload = Partial<Applet['header']>;

type Header = {
  title?: string;
  onClose?: Message[];
  onBack?: Message[];
  infoIcon?: {
    title: string;
  };
  statusTag?: {
    status: StatusTagStatus;
    message: string;
  };
};

type AppletHeader = Omit<Header, 'onClose' | 'onBack'> & {
  onClose?: () => void;
  onBack?: () => void;
};

export type Applet = AppletSpec.Applet & {
  layout: Layout;
  title: string;
  label?: string;
  onClose?: () => void;
  header?: AppletHeader;
};

export const id = 'call-frame-applet';

export enum Events {
  CustomEvent = 'call-frame-applet:custom-event',
}
export enum Actions {
  Attach = 'call-frame-applet:attach',
  Detach = 'call-frame-applet:detach',
  UpdateHeader = 'call-frame-applet:update-header',
}

export type CustomEventPayload = unknown;

const VALID_CONFIG_KEYS = [
  ...AppletSpec.VALID_CONFIG_KEYS,
  'label',
  'attach',
  'onClose',
  'layout',
  'header',
] as const;
const VALID_HEADER_CONFIG_KEYS = [
  'title',
  'onClose',
  'onBack',
  'infoIcon',
  'statusTag',
] as const;
const VALID_INFO_ICON_CONFIG_KEYS = ['title'] as const;
const VALID_STATUS_TAG_CONFIG_KEYS = ['status', 'message'] as const;

export type Config = AppletSpec.Config & {
  type: typeof id;
  /**
   * @deprecated Use `header.title` instead.
   */
  label?: string;
  attach?: boolean;
  header?: Header;
  /**
   * @deprecated Use `header.onClose` instead.
   */
  onClose?: Message[];
  layout?: Layout;
};

export type Constraints = ExtendedConstraints<{
  events: Events;
  actions: Actions;
  config: Config;
  eventPayloadTuple: [Events.CustomEvent, CustomEventPayload];
  eventHandlerTuple: [
    Events.CustomEvent,
    (payload: CustomEventPayload) => void,
  ];

  actionPayloadTuple:
    | [Actions.Attach]
    | [Actions.Detach]
    | [Actions.Attach, AppletSpec.AppletPayload]
    | [Actions.Detach, AppletSpec.AppletPayload]
    | [Actions.UpdateHeader, UpdateHeaderPayload];
}>;

export function isValidHeaderConfig(
  header: Record<string, unknown>,
): header is Header {
  if (!hasOnlyKeysFromSet(header, VALID_HEADER_CONFIG_KEYS)) {
    return false;
  }

  const isHeaderTitleNotValid =
    hasValue(header, 'title') && typeof header.title !== 'string';

  const isHeaderOnCloseNotValid =
    hasValue(header, 'onClose') &&
    (!Array.isArray(header.onClose) ||
      !header.onClose.every((m) => isValidMessageConfig(m)));

  const isHeaderOnBackNotValid =
    hasValue(header, 'onBack') &&
    (!Array.isArray(header.onBack) ||
      !header.onBack.every((m) => isValidMessageConfig(m)));

  const isInfoIconNotValid =
    hasValue(header, 'infoIcon') &&
    (typeof header.infoIcon !== 'object' ||
      !hasOnlyKeysFromSet(
        header['infoIcon'] as NonNullable<Header['infoIcon']>,
        VALID_INFO_ICON_CONFIG_KEYS,
      ) ||
      typeof (header['infoIcon'] as NonNullable<Header['infoIcon']>).title !==
        'string');

  const isStatusTagNotValid =
    hasValue(header, 'statusTag') &&
    (typeof header.statusTag !== 'object' ||
      !hasOnlyKeysFromSet(
        header['statusTag'] as NonNullable<Header['statusTag']>,
        VALID_STATUS_TAG_CONFIG_KEYS,
      ) ||
      typeof (header['statusTag'] as NonNullable<Header['statusTag']>)
        .message !== 'string' ||
      typeof (header['statusTag'] as NonNullable<Header['statusTag']>)
        .status !== 'string' ||
      !validStatusTagStatuses.includes(
        (header['statusTag'] as NonNullable<Header['statusTag']>).status,
      ));

  return !(
    isHeaderTitleNotValid ||
    isHeaderOnCloseNotValid ||
    isHeaderOnBackNotValid ||
    isInfoIconNotValid ||
    isStatusTagNotValid
  );
}

export function isValidConfig(config: CapabilityConfig): config is Config {
  if (!AppletSpec.isValidConfig(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (config.type !== id) {
    return false;
  }

  const isLabelNotValid =
    hasValue(config, 'label') && typeof config.label !== 'string';

  const isAttachNotValid =
    hasValue(config, 'attach') && typeof config.attach !== 'boolean';

  const isOnCloseNotValid =
    hasValue(config, 'onClose') &&
    (!Array.isArray(config.onClose) ||
      !config.onClose.every((m) => isValidMessageConfig(m)));

  const isOnBackNotValid =
    hasValue(config, 'onBack') &&
    (!Array.isArray(config.onBack) ||
      !config.onBack.every((m) => isValidMessageConfig(m)));

  const isLayoutNotValid =
    hasValue(config, 'layout') &&
    (typeof config.layout !== 'string' ||
      !appletLayoutValues.includes(config.layout));

  const isHeaderNotValid =
    hasValue(config, 'header') &&
    (typeof config.header !== 'object' ||
      !isValidHeaderConfig(config.header as Record<string, unknown>));

  if (
    isLabelNotValid ||
    isAttachNotValid ||
    isOnCloseNotValid ||
    isOnBackNotValid ||
    isLayoutNotValid ||
    isHeaderNotValid
  ) {
    return false;
  }

  return true;
}
